// automationsList.js — automations library
//
// Uses ActiveHelper/MainPage for consistency. Enable toggle is the kill switch —
// flipping disabled instantly stops the automation from firing.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ActiveHelper from '../../../../components/common/activeHelper';
import { firestore } from '../../../../helpers/Firebase';
import { actorFromUser, writeAuditEntry, AuditLogModal, useAuditLogModal } from '../shared/auditLog.js';

// TRIGGER_LABELS map + Trigger column removed on 2026-08-01 with the
// trigger→ruleTree refactor. Automations fire from their ruleTree only;
// there's no `trigger` field on the doc anymore.

const AutomationsList = ({ history }) => {
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';

  const [automations, setAutomations] = useState([]);
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    const unsub = firestore()
      .collection('marketing_automations')
      .orderBy('updatedAt', 'desc')
      .onSnapshot(
        (snap) => setAutomations(snap.docs.map(d => ({ id: d.id, key: d.id, ...d.data() }))),
        (err) => console.error('automations listener error', err)
      );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!search || !search.trim()) { setFiltered(automations); return; }
    const q = search.toLowerCase().trim();
    setFiltered(automations.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q)
    ));
  }, [automations, search]);

  const decorated = useMemo(() => filtered.map(a => ({
    ...a,
    _runs: a.stats?.runs || 0,
    _sent: a.stats?.totalSent || 0,
    _updatedAt: a.updatedAt || a.createdAt,
  })), [filtered]);

  const tableColumns = useMemo(() => [
    // Inline switch — flips `active` on the marketing_automations doc without
    // opening the editor. Handler defined below via customSwitchFunction.
    // Placed first so marketing can enable/disable at a glance.
    { Header: 'Active', accessor: 'active', width: 90, type: 'switch' },
    { Header: 'Name', accessor: 'name', type: 'label' },
    { Header: 'Runs', accessor: '_runs', type: 'label' },
    { Header: 'Total sent', accessor: '_sent', type: 'label' },
    {
      Header: 'Last updated',
      accessor: '_updatedAt',
      type: 'label',
      formatValue: (v) => v ? moment(v).fromNow() : '—',
    },
  ], []);

  const handleEdit = useCallback((a) => {
    if (!a) return;
    history.push(`/app/marketing/automations/${a.id || a.key}/edit`);
  }, [history]);

  const handleCreate = useCallback(() => history.push('/app/marketing/automations/new'), [history]);

  const handleDelete = useCallback(async (a) => {
    if (!a) return;
    const id = a.id || a.key;
    try {
      // Audit BEFORE the doc is gone (subcollection stays even after parent
      // delete but avoiding the race is cheap and clean).
      writeAuditEntry('marketing_automations', id, {
        ...actorFromUser(user),
        action: 'deleted',
        notes: `Deleted automation "${a.name || id}"`,
      });
      await firestore().collection('marketing_automations').doc(id).delete();
    } catch (e) {
      console.error('automation delete failed', e);
    }
  }, [user]);

  const logsModal = useAuditLogModal();

  // Inline row switch → flip `active` on the automation doc.
  // Overrides MainPage's default which writes to firestore().collection(selected),
  // since our `selected="automations"` label doesn't match the actual collection.
  const handleSwitchActive = useCallback(async (row, checked) => {
    if (!row) return;
    const id = row.id || row.key;
    try {
      await firestore().collection('marketing_automations').doc(id).update({
        active: !!checked,
        updatedAt: Date.now(),
      });
      writeAuditEntry('marketing_automations', id, {
        ...actorFromUser(user),
        action: checked ? 'active' : 'inactive',
      });
    } catch (e) {
      console.error('automation switch failed', e);
    }
  }, [user]);

  return (
    <>
    <ActiveHelper
      title="Marketing Automations"
      selected="automations"
      data={decorated}
      tableColumns={tableColumns}
      customActionButtons={[{
        accessor: 'logs',
        title: 'Logs',
        color: 'info',
        func: (row) => logsModal.open(row),
      }]}
      permenatDelete={true}
      defaultSorting="_updatedAt"
      sortDirection="desc"
      customCreateFunction={handleCreate}
      customEditFunction={handleEdit}
      customDeleteFunction={handleDelete}
      confirmCustomDelete={true}
      customSwitchFunction={handleSwitchActive}
      showCreateButton={true}
      user={user}
    />
    <AuditLogModal
      isOpen={logsModal.isOpen}
      dismiss={logsModal.close}
      collection="marketing_automations"
      docId={logsModal.row?.id || logsModal.row?.key}
      title={logsModal.row?.name}
    />
    </>
  );
};

export default AutomationsList;
